// SPDX-License-Identifier: GPL-3.0-only
// PC ALA3/IABL and generic scene-curve evaluation. OpenSaga nuanim3.cpp
// ANI_Ani3ExtractAllNodeCurves, nucore_plain.cpp NuAnimCurve2SetApplyToMatrix_3,
// gameanim.cpp EvalAnim; BrickBench DINIBlock/TerrainLoader platform binding.
import {evaluatePose} from './pose.mjs';

const identity = () => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

export function parseSceneAnimations(scene) {
  if (!scene?.buffer || !Array.isArray(scene.specialObjects)) throw new Error('Parsed original scene required.');
  const view = new DataView(scene.buffer), bytes = new Uint8Array(scene.buffer);
  function range(o, n) {
    if (!Number.isSafeInteger(o) || !Number.isSafeInteger(n) || o < 0 || n < 0 || o + n > bytes.length)
      throw new Error('Scene animation data out of bounds.');
    return o;
  }
  const u32 = o => view.getUint32(range(o, 4), true), u16 = o => view.getUint16(range(o, 2), true);
  const u8 = o => view.getUint8(range(o, 1));
  const f32 = o => {const v = view.getFloat32(range(o, 4), true); if (!Number.isFinite(v)) throw new Error('Nonfinite scene animation value.'); return v;};
  const blocks = new Map();
  for (let o = u32(0) + 4 + 32; o + 8 <= bytes.length;) {
    const tag = String.fromCharCode(...bytes.subarray(o, o + 4)), length = u32(o + 4);
    if (length < 8) throw new Error('Invalid scene animation block.');
    range(o, length); blocks.set(tag, {start: o + 8, end: o + length});
    if (tag === 'PNTR') break;
    o += length;
  }
  const ala = blocks.get('ALA3'), iabl = blocks.get('IABL'), animations = new Map();
  const count = ala ? u32(ala.start) : 0;
  if (count > 65535 || (ala && ala.start + 8 + count * 4 > ala.end)) throw new Error('Invalid scene animation count.');
  function animation(index) {
    if (!ala || index >= count) throw new Error('Invalid scene animation index.');
    if (animations.has(index)) return animations.get(index);
    const address = u32(ala.start + 8 + index * 4);
    if (!address) {animations.set(index, null); return null;}
    if (address < ala.start || address + 60 > ala.end) throw new Error('Invalid embedded animation pointer.');
    const magic = u32(address);
    if (![0x414e4934, 0x414e4935].includes(magic)) throw new Error('Unsupported embedded scene animation magic.');
    const nodes = u16(address + 4), keyCount = u16(address + 6), stride = u16(address + 8),
      frames = u16(address + 10), curvesCount = u16(address + 12), firstFrame = u16(address + 14);
    if (nodes !== 1 || curvesCount !== 9 || !keyCount || !frames || (keyCount > 1 && frames < 2) || u16(address + 20))
      throw new Error('Unsupported embedded scene animation layout.');
    const minimum = f32(address + 28), scale = f32(address + 32),
      quant = u32(address + 36), constants = u32(address + 40), types = u32(address + 44),
      keys = u32(address + 48), flags = u32(address + 52), constantOffset = u8(address + 17) - 16;
    function inside(o, n) {range(o, n); if (o < address || o + n > ala.end) throw new Error('Invalid scene curve pointer.');}
    inside(types, 27); inside(flags, 1);
    const curves = []; let keyOffset = 0, quantIndex = 0;
    for (let c = 0; c < 9; c++) {
      const type = u16(types + c * 2), curve = {type, keyOffset, forceZero: !!(u8(types + 18 + c) & 1)};
      if (type === 6 || type === 7) {
        inside(quant + quantIndex * 8, 8);
        curve.scale = f32(quant + quantIndex * 8); curve.minimum = f32(quant + quantIndex * 8 + 4);
        quantIndex++; keyOffset += type === 6 ? 4 : 8;
      } else if (type === 8 || type === 10) keyOffset += 4;
      else if (type >= 16) {
        const offset = constants + (constantOffset + type) * 2;
        inside(offset, 2); curve.value = u16(offset) * scale + minimum;
      } else throw new Error(`Unsupported generic scene curve ${type}.`);
      curves.push(curve);
    }
    if (keyOffset !== stride) throw new Error('Scene animation stride mismatch.');
    const result = {index, address, keyCount, stride, frames, firstFrame, declaredEndFrame: u16(address + 22), curves, flags: u8(flags), keys, constants, inside};
    animations.set(index, result);
    return result;
  }
  function values(anim, frame) {
    const key = anim.keyCount === 1 ? 0 : Math.max(0, Math.min(anim.keyCount - 1,
      (frame - 1 - anim.firstFrame) * (anim.keyCount - 1) / (anim.frames - 1)));
    const whole = Math.floor(key), quarter = whole & 3, fraction = key - whole;
    return anim.curves.map(curve => {
      if (curve.type >= 16) return curve.value;
      const o = anim.keys + (whole >> 2) * anim.stride + curve.keyOffset,
        next = o + anim.stride, f = curve.forceZero ? 0 : fraction;
      let value;
      if (curve.type === 8 || curve.type === 10) {
        anim.inside(o + quarter, 1); const index = u8(o + quarter);
        anim.inside(anim.constants + index * 2, curve.type === 8 ? 2 : 4);
        if (curve.type === 8) value = view.getInt16(anim.constants + index * 2, true);
        else {
          const storage = new DataView(new ArrayBuffer(4));
          storage.setUint32(0, (u16(anim.constants + index * 2) << 16) | u16(anim.constants + (index + 1) * 2), true);
          value = storage.getFloat32(0, true);
        }
      } else if (curve.type === 6) {
        anim.inside(o, 4); anim.inside(next, 4);
        const word = u32(o), nw = u32(next), start = word & 255, end = nw & 255;
        const t0 = ((word >>> (8 + quarter * 6)) & 63) * 0.01587302;
        if (quarter === 3) {
          const a = start + (end - start) * t0;
          if (f === 0) value = a;
          else {anim.inside(next + anim.stride, 1); value = a + (end +
            (u8(next + anim.stride) - end) * ((nw >>> 8) & 63) * 0.01587302 - a) * f;}
        } else {
          const t1 = ((word >>> (8 + (quarter + 1) * 6)) & 63) * 0.01587302;
          value = start + (end - start) * (t0 + (t1 - t0) * f);
        }
        value = value * curve.scale + curve.minimum;
      } else {
        anim.inside(o, 8); anim.inside(next, 2);
        const start = u16(o), end = u16(next), a = u16(o + 2), b = u16(o + 4), c = u16(o + 6);
        const knots = [a & 4095, b & 4095, c & 4095, ((b & 0xf000) >>> 8) | ((c & 0xf000) >>> 4) | (a >>> 12)];
        const v0 = start + (end - start) * knots[quarter] / 4095;
        let v1 = v0;
        if (f !== 0) {
          if (quarter === 3) {anim.inside(next + 2, 2); anim.inside(next + anim.stride, 2);
            v1 = end + (u16(next + anim.stride) - end) * (u16(next + 2) & 4095) / 4095;}
          else v1 = start + (end - start) * knots[quarter + 1] / 4095;
        }
        value = (v0 + (v1 - v0) * f) * curve.scale + curve.minimum;
      }
      if (!Number.isFinite(value)) throw new Error('Scene animation sample overflow.');
      return value;
    });
  }
  const stateAnimations = [], sali = blocks.get('SALI');
  if (sali) {
    const n = u32(sali.start), references = u32(sali.start + 4);
    if (n > 65535 || sali.start + 8 + n * 12 > sali.end) throw new Error('Invalid SALI animation table.');
    if (references > 1) for (let i = 0; i < n; i++) {
      const o = sali.start + 8 + i * 12, count = u16(o), times = u32(o + 4), values = u32(o + 8);
      if (!count) {stateAnimations.push(null); continue;}
      range(times, count * 4); range(values, count);
      const entries = Array.from({length: count}, (_, j) => ({frame: f32(times + j * 4), value: u8(values + j)}));
      if (entries.some((e, j) => j > 0 && e.frame < entries[j - 1].frame)) throw new Error('Unordered SALI state times.');
      stateAnimations.push({endFrame: u16(o + 2), entries});
    }
  }
  const records = [], byName = new Map(), unsupported = [];
  for (let index = 0; index < scene.specialObjects.length; index++) {
    const special = scene.specialObjects[index], o = special.address;
    range(o, 0xd0);
    const originalMatrix = Array.from({length: 16}, (_, i) => f32(o + i * 4));
    const pointer = u32(o + 0xc4), binding = {index, name: special.name, originalMatrix, localSphere: special.localSphere ?? null, localBounds: special.localBounds ?? null,
      boundingBoxIndex: u32(o + 0xc0), hasAnimation: false, timeFactor: 1, currentFrame: 1, initialVisible: special.visible};
    let anim = null;
    if (pointer && pointer !== 0xffffffff && iabl && pointer >= iabl.start + 8 && pointer + 0x60 <= iabl.end &&
        (pointer - iabl.start - 8) % 0x60 === 0) {
      const animationIndex = u16(pointer + 0x5c);
      try {
        anim = animation(animationIndex);
        binding.hasAnimation = !!anim; binding.animationIndex = animationIndex;
        binding.currentFrame = f32(pointer + 0x4c);
        binding.timeFactor = f32(pointer + 0x54) || 1;
        binding.endFrame = anim ? anim.declaredEndFrame || anim.firstFrame + anim.frames : 0;
        const flags = u32(pointer + 0x50), stateIndex = (flags >>> 22) - 1;
        binding.instanceAddress = pointer;
        binding.playback = {frame: binding.currentFrame, flags, tfactor: f32(pointer + 0x40),
          first: f32(pointer + 0x44), interval: f32(pointer + 0x48), previousFrame: f32(pointer + 0x58)};
        binding.stateAnimation = stateIndex < 0 ? null : stateAnimations[stateIndex] ?? null;
        if (stateIndex >= 0 && !binding.stateAnimation) binding.ambientUnsupported = 'missing SALI state-animation binding';
      } catch (error) {
        binding.unsupported = String(error.message || error);
        unsupported.push({index, name: binding.name, animationIndex, reason: binding.unsupported});
      }
    }
    binding.sample = frame => {
      if (!Number.isFinite(frame)) throw new Error('Invalid scene animation frame.');
      if (binding.unsupported) throw new Error(binding.unsupported);
      if (!anim) return originalMatrix.slice();
      const v = values(anim, frame), pose = {rotationFormat: 'euler', nodes: [{translation: v.slice(0, 3),
        rotation: v.slice(3, 6), scale: v.slice(6), flags: (anim.flags & 9) | 2}]};
      const matrix = evaluatePose({joints: [{parent: -1, inverseBindMatrix: identity()}]}, pose).absoluteMatrices[0];
      // EvalAnim adds only original instance translation, not its rotation.
      for (let axis = 0; axis < 3; axis++) matrix[12 + axis] += originalMatrix[12 + axis];
      return matrix;
    };
    records.push(binding); if (binding.name) byName.set(binding.name.toLowerCase(), binding);
  }
  const terrainBindings = new Map(), inid = blocks.get('INID');
  if (inid) {
    const n = u32(inid.start); if (n > 1000000 || inid.start + 8 + n * 2 > inid.end) throw new Error('Invalid DINI platform map.');
    for (let i = 0; i < n; i++) {
      const specialIndex = view.getInt16(inid.start + 8 + i * 2, true);
      const record = records.find(r => r.boundingBoxIndex === specialIndex);
      if (record) terrainBindings.set(i, {name: record.name, specialIndex: record.index, boundingBoxIndex: specialIndex});
    }
  }
  return {records, animations, terrainBindings, unsupported, stateAnimations, resolve: name => byName.get(String(name).toLowerCase()) ?? null,
    sample(name, frame) {const record = this.resolve(name); if (!record) throw new Error(`Unknown original scene object ${name}.`); return record.sample(frame);}};
}

export function createSceneAnimationAdapter(scene, bindings = null) {
  const parsed = parseSceneAnimations(scene);
  const ambientRecords = parsed.records.filter(r => r.playback).sort((a, b) => a.instanceAddress - b.instanceAddress);
  return {...parsed, apply(name, frame) {const matrix = parsed.sample(name, frame); bindings?.setMatrix(name, matrix); return matrix;},
    setMatrix(name, matrix) {bindings?.setMatrix(name, matrix);},
    setVisible(name, visible) {bindings?.setVisible(name, visible);},
    updateAmbient(dt, {ownedNames = new Set()} = {}) {
      if (!Number.isFinite(dt) || dt < 0 || dt > 1) throw new Error('Invalid ambient animation time.');
      const owned = new Set([...ownedNames].map(n => n.toLowerCase()));
      let updated = 0;
      for (const record of ambientRecords) {
        if (owned.has(record.name.toLowerCase()) || record.unsupported || record.ambientUnsupported) continue;
        const playback = record.playback, table = record.stateAnimation;
        const visible = bindings?.getObject?.(record.name)?.visible ?? record.initialVisible;
        if (!table && (!visible || !record.hasAnimation)) continue;
        const end = record.endFrame || table?.endFrame;
        if (!end) continue;
        let frame = playback.frame;
        if (playback.flags & 1) {
          playback.frame += dt * 60 * playback.tfactor;
          if (playback.flags & 4) {
            if (playback.frame < playback.first) continue;
            playback.flags &= ~4; playback.frame -= playback.first - 1;
          }
          frame = playback.frame;
          const intervalEnd = playback.interval + end;
          if (frame >= intervalEnd) {
            if (playback.flags & 8) {
              const length = intervalEnd - 1;
              if (!(length > 0)) {record.ambientUnsupported = 'nonpositive repeating animation interval'; continue;}
              playback.frame -= length * Math.floor((playback.frame - 1) / length); frame = playback.frame;
            } else {playback.flags &= ~1; playback.frame = end; frame = end;}
          } else if (frame > end) frame = end;
          else if (frame < 1) {playback.flags &= ~1; playback.frame = 1; frame = 1;}
          if (playback.flags & 2) frame = end + 1 - frame;
        }
        if (frame !== playback.previousFrame) {
          let evaluateMatrix = true;
          if (table) {
            let index = (playback.flags & 0x003fc000) >>> 14;
            index = Math.min(index, table.entries.length - 1);
            const old = table.entries[index].value;
            while (index > 0 && frame < table.entries[index].frame) index--;
            while (index + 1 < table.entries.length && frame >= table.entries[index + 1].frame) index++;
            const value = table.entries[index].value;
            playback.flags = (playback.flags & ~0x003fc000) | (index << 14);
            if (value !== old) {record.initialVisible = value !== 0; bindings?.setVisible(record.name, value !== 0); if (value === 0) evaluateMatrix = false;}
          }
          if (record.hasAnimation && evaluateMatrix) bindings?.setMatrix(record.name, record.sample(frame));
          playback.previousFrame = frame; updated++;
        }
        record.currentFrame = playback.frame; record.evaluatedFrame = frame;
      }
      return {updated, unsupported: ambientRecords.filter(r => r.ambientUnsupported).map(r => ({name: r.name, reason: r.ambientUnsupported}))};
    }};
}
